import React, { useState, useCallback, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, Switch, Alert, LayoutAnimation, Linking, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../../components/common/SvgIcon';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import { Input } from '../../components/common/Input';
import { WheelPicker } from '../../components/common/wheel-picker/WheelPicker';
import { appTheme } from '../../shared/theme';
import type { ThemeColors } from '../../shared/theme';
import { triggerHaptic } from '../../shared/utils/haptics';
import type { DashboardReminder as ReminderType } from '../../data/domains/dashboard';
import {
  REMINDER_PRESETS,
  REMINDER_ICON_OPTIONS,
  REMINDER_COLOR_OPTIONS,
  REMINDER_INTERVAL_OPTIONS,
} from '../../data/domains/dashboard';
import { requestReminderPermissions, createReminderChannel, isReminderNotificationsAvailable } from '../../data/domains/dashboard';
import { useTranslation, tPlural } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { useAuthSheetKeyboardInset } from '../../shared/auth';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { DASH_STUDIO_RADIUS, studioCardShell } from '../../shared/theme/studio/dashboardConstants';
import { hexToRgba } from '../../shared/theme/colorUtils';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../../shared/theme/sharedStyles';

const card_padding = appTheme.card.padding.lg;

type CustomReminderPayload = {
  label: string;
  icon: string;
  icon_color: string | null;
  interval_minutes: number;
};

interface DashboardRemindersProps {
  colors: ThemeColors;
  reminders: ReminderType[];
  on_add: (preset_id: string, custom_minutes?: number, label?: string) => void;
  on_add_custom: (opts: { label: string; icon: string; icon_color?: string | null; interval_minutes: number }) => void;
  on_remove: (id: string) => void;
  on_toggle: (id: string, enabled: boolean) => void;
}

const default_custom: CustomReminderPayload = {
  label: '',
  icon: 'bell',
  icon_color: REMINDER_COLOR_OPTIONS[0],
  interval_minutes: REMINDER_INTERVAL_OPTIONS[2].value,
};

type AddReminderStep = 'preset' | 'custom';

function reminderIntervalIndex(minutes: number): number {
  const idx = REMINDER_INTERVAL_OPTIONS.findIndex(opt => opt.value === minutes);
  return idx >= 0 ? idx : 2;
}

export function DashboardReminders({ colors, reminders, on_add, on_add_custom, on_remove, on_toggle }: DashboardRemindersProps) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const insets = useSafeAreaInsets();
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';
  const border_muted = (hexToRgba(colors.text, 0.12) as string) ?? colors.border;
  const disclosure = rtl.trailingRowDisclosureChevron;
  const keyboard_inset = useAuthSheetKeyboardInset();
  const [expanded, set_expanded] = useState(true);
  const [add_modal_visible, set_add_modal_visible] = useState(false);
  const [add_modal_step, set_add_modal_step] = useState<AddReminderStep>('preset');
  const [custom_form, set_custom_form] = useState<CustomReminderPayload>(default_custom);
  const [custom_scroll_enabled, set_custom_scroll_enabled] = useState(true);
  const custom_scroll_ref = useRef<ScrollView>(null);

  const footer_safe_pad = Math.max(insets.bottom, appTheme.spacing.md);

  const cancel_secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(colors),
      label: createStackedFooterSecondaryLabelStyle(colors),
    }),
    [colors],
  );
  const save_primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(colors),
      label: createStackedFooterPrimaryLabelStyle(colors),
    }),
    [colors],
  );

  const toggle_expanded = useCallback(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    set_expanded(prev => !prev);
    triggerHaptic('impactLight');
  }, []);

  const open_add = useCallback(() => {
    triggerHaptic('impactLight');
    set_add_modal_step('preset');
    set_add_modal_visible(true);
    if (!isReminderNotificationsAvailable()) {
      return;
    }
    requestReminderPermissions()
      .then(granted => {
        createReminderChannel();
        if (!granted) {
          Alert.alert(t('reminders.notificationsNeededTitle'), t('reminders.notificationsNeededMessage'), [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('reminders.openSettings'), onPress: () => Linking.openSettings() },
          ]);
        }
      })
      .catch(() => {});
  }, [t]);

  const close_add_modal = useCallback(() => {
    set_add_modal_visible(false);
    set_add_modal_step('preset');
    set_custom_scroll_enabled(true);
  }, []);

  const open_custom_form = useCallback(() => {
    triggerHaptic('impactLight');
    set_custom_form(default_custom);
    set_add_modal_step('custom');
    requestAnimationFrame(() => {
      custom_scroll_ref.current?.scrollTo({ y: 0, animated: false });
    });
  }, []);

  const format_interval = useCallback(
    (minutes: number) => {
      if (minutes >= 24 * 60) {
        return t('reminders.daily');
      }
      if (minutes >= 60) {
        return t('reminders.everyHours').replace('{{hours}}', String(minutes / 60));
      }
      return t('reminders.everyMinutes').replace('{{minutes}}', String(minutes));
    },
    [t],
  );

  const preset_label = useCallback((label_key: string) => t(label_key), [t]);

  const add_from_preset = useCallback(
    (preset_id: string, label_key: string) => {
      on_add(preset_id, undefined, preset_label(label_key));
      close_add_modal();
    },
    [on_add, preset_label, close_add_modal],
  );

  const save_custom_reminder = useCallback(() => {
    const label = custom_form.label.trim() || t('reminders.reminderDefaultLabel');
    on_add_custom({
      label,
      icon: custom_form.icon,
      icon_color: custom_form.icon_color,
      interval_minutes: custom_form.interval_minutes,
    });
    close_add_modal();
  }, [custom_form, on_add_custom, close_add_modal, t]);

  const interval_index = useMemo(() => reminderIntervalIndex(custom_form.interval_minutes), [custom_form.interval_minutes]);

  const interval_wheel_theme = useMemo(
    () => ({
      fillContainer: false,
      showExpandButton: false,
      readoutGutterV: appTheme.spacing.xs,
      readoutGutterBottom: appTheme.spacing.xxs,
      scrollableAreaHeight: Platform.OS === 'android' ? 72 : 56,
      lineColor: colors.border,
      bigLineColor: colors.primary,
      countColor: 'transparent',
      gradientAccentColor: 'transparent',
      backgroundColor: 'transparent',
      scaleMarkerColor: colors.primary,
      idleSliderArrowsAfterMs: 4000,
      idleSliderArrowsColor: colors.textSecondary,
      fixedSpacePerLine: rtl.isRTL ? 40 : 36,
      bigLineEvery: 1,
      lineCanvasPaddingTop: Platform.OS === 'android' ? 2 : 3,
    }),
    [colors.border, colors.primary, colors.textSecondary, rtl.isRTL],
  );

  const on_interval_index_change = useCallback((idx: number) => {
    const opt = REMINDER_INTERVAL_OPTIONS[idx];
    if (opt) {
      set_custom_form(f => ({ ...f, interval_minutes: opt.value }));
    }
  }, []);

  const icon_color_for = (r: ReminderType) => r.icon_color ?? colors.primary;

  const confirm_remove = useCallback(
    (id: string, label: string) => {
      Alert.alert(t('reminders.deleteReminder'), t('reminders.removeReminderConfirm').replace('{{label}}', label), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('order.delete'), style: 'destructive', onPress: () => on_remove(id) },
      ]);
    },
    [on_remove, t],
  );

  return (
    <View>
      {reminders.length === 0 ? (
        <Clickable
          onPress={open_add}
          accessibilityLabel={t('reminders.addFirstReminder')}
          accessibilityRole="button"
          pressScale={PRESS_PRESETS.listRow.scale}
          haptics>
          <View
            style={[
              styles.card,
              appTheme.shadows.sm,
              studioCardShell(colors),
              { backgroundColor: colors.surface },
            ]}>
            <View style={[styles.emptyBlock, { marginTop: 0 }]}>
              <View style={[styles.emptyIconCircle, { backgroundColor: colors.primaryUltra || colors.primaryFade }]}>
                <SvgIcon name="plus" size={28} color={colors.primary} />
              </View>
              <Text
                style={[styles.emptyHeading, { color: colors.text, textAlign: 'center', writingDirection: rtl.isRTL ? 'rtl' : 'ltr' }]}>
                {t('reminders.noRemindersYet')}
              </Text>
              <Text
                style={[
                  styles.emptyHint,
                  { color: colors.textSecondary, textAlign: 'center', writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                ]}>
                {t('reminders.tapToCreate')}
              </Text>
            </View>
          </View>
        </Clickable>
      ) : (
        <View
          style={[
            styles.card,
            appTheme.shadows.sm,
            studioCardShell(colors),
            { backgroundColor: colors.surface },
          ]}>
          <View style={[styles.topRow, !expanded && styles.topRowCollapsed]}>
            <Clickable
              onPress={toggle_expanded}
              accessibilityLabel={expanded ? t('reminders.collapseReminders') : t('reminders.expandReminders')}
              accessibilityRole="button"
              pressScale={PRESS_PRESETS.listRow.scale}
              haptics
              style={styles.headerTouchable}>
              <View style={styles.expandedHeaderText}>
                <Text
                  style={[
                    styles.inCardRemindersTitle,
                    { color: colors.text, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                  ]}
                  numberOfLines={1}
                  accessibilityRole="header">
                  {t('reminders.title')}
                </Text>
                <Text
                  style={[
                    styles.cardSubtitle,
                    { color: colors.textSecondary, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                  ]}
                  numberOfLines={2}>
                  {tPlural('reminders.subtitleActive', reminders.length)}
                </Text>
              </View>
            </Clickable>
            <Clickable
              onPress={open_add}
              accessibilityLabel={t('reminders.addReminder')}
              accessibilityRole="button"
              pressScale={PRESS_PRESETS.button.scale}
              haptics>
              <View style={[styles.addPill, { backgroundColor: (colors.primaryUltra as string) || (colors.primaryFade as string) }]}>
                <SvgIcon name="plus" size={22} color={colors.primary} />
              </View>
            </Clickable>
          </View>
          {expanded && (
            <View style={styles.list}>
              {reminders.map((r, index) => (
                <Clickable
                  key={r.id}
                  onLongPress={() => {
                    triggerHaptic('impactMedium');
                    confirm_remove(r.id, r.label);
                  }}
                  delayLongPress={400}
                  style={styles.reminderRowClickable}>
                  <View
                    style={styles.row}>
                    <View style={[styles.rowIcon, { backgroundColor: (icon_color_for(r) || colors.primary) + '18' }]}>
                      <SvgIcon name={r.icon as any} size={18} color={icon_color_for(r) || colors.primary} />
                    </View>
                    <View style={styles.rowBody}>
                      <Text
                        style={[
                          styles.rowLabel,
                          { color: colors.text, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                        ]}
                        numberOfLines={1}>
                        {r.label}
                      </Text>
                      <Text
                        style={[
                          styles.rowMeta,
                          { color: colors.textSecondary, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                        ]}
                        numberOfLines={1}>
                        {format_interval(r.interval_minutes)}
                      </Text>
                    </View>
                    <Switch
                      value={r.enabled}
                      onValueChange={v => on_toggle(r.id, v)}
                      thumbColor={r.enabled ? colors.white : colors.primaryFade}
                      trackColor={{ false: colors.grey, true: colors.primary }}
                    />
                  </View>
                </Clickable>
              ))}
            </View>
          )}
        </View>
      )}

      <BottomSheetPopup
        visible={add_modal_visible}
        onClose={close_add_modal}
        title={add_modal_step === 'preset' ? t('reminders.addReminderModalTitle') : t('reminders.customReminderTitle')}
        showDragHandle
        animationType="fade"
        keyboardAvoiding={add_modal_step === 'custom'}
        padBottomSafeArea={false}
        headerStyle={{ backgroundColor: colors.background, borderBottomWidth: 0 }}
        sheetStyle={{ maxHeight: add_modal_step === 'custom' ? '88%' : '72%', backgroundColor: colors.background }}>
        {add_modal_step === 'preset' ? (
          <View style={styles.sheetAddRoot}>
            <View style={styles.sheetListIntro}>
              <Text
                style={[
                  styles.sheetKicker,
                  { color: colors.textLight, textAlign: text_align, writingDirection: rtl.writingDirection },
                ]}
                numberOfLines={1}>
                {t('reminders.presetKicker')}
              </Text>
              <Text
                style={[
                  styles.sheetLead,
                  { color: colors.textSecondary, textAlign: text_align, writingDirection: rtl.writingDirection },
                ]}
                numberOfLines={2}>
                {t('reminders.presetListLead')}
              </Text>
            </View>
            <ScrollView
              style={styles.presetScroll}
              contentContainerStyle={styles.presetScrollContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              bounces
              overScrollMode="always">
              <View style={[styles.sheetGroup, { backgroundColor: colors.surface }]}>
                {REMINDER_PRESETS.map((item, index) => {
                  const label = preset_label(item.label_key);
                  return (
                    <View key={item.id}>
                      {index > 0 ? <View style={styles.groupSep} /> : null}
                      <Clickable
                        onPress={() => add_from_preset(item.id, item.label_key)}
                        accessibilityLabel={t('reminders.addPresetA11y').replace('{{label}}', label)}
                        accessibilityRole="button"
                        pressScale={PRESS_PRESETS.listRow.scale}
                        haptics
                        style={styles.groupRowClickable}
                        contentStyle={styles.groupRowClickableContent}>
                        <View style={styles.groupRow}>
                          <View
                            style={[
                              styles.groupIcon,
                              { backgroundColor: (colors.primaryUltra as string) || (colors.primaryFade as string) },
                            ]}>
                            <SvgIcon name={item.icon as any} size={24} color={colors.primary} />
                          </View>
                          <View style={styles.presetText}>
                            <Text
                              style={[
                                styles.groupTitle,
                                { color: colors.text, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                              ]}
                              numberOfLines={2}>
                              {label}
                            </Text>
                            <Text
                              style={[
                                styles.groupMeta,
                                { color: colors.textSecondary, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                              ]}
                              numberOfLines={1}>
                              {format_interval(item.interval_minutes)}
                            </Text>
                          </View>
                          <View style={styles.chevronWrap}>
                            <SvgIcon name={disclosure} size={18} color={colors.textLight} />
                          </View>
                        </View>
                      </Clickable>
                    </View>
                  );
                })}
              </View>
              <Clickable
                onPress={open_custom_form}
                accessibilityLabel={t('reminders.createCustomReminder')}
                accessibilityRole="button"
                pressScale={PRESS_PRESETS.listRow.scale}
                haptics
                style={styles.customBlock}
                contentStyle={styles.groupRowClickableContent}>
                <View
                  style={[
                    styles.customCreateCard,
                    { backgroundColor: hexToRgba(colors.primary, 0.09) as string },
                  ]}>
                  <View style={[styles.customIconBox, { backgroundColor: hexToRgba(colors.primary, 0.18) as string }]}>
                    <SvgIcon name="plus" size={24} color={colors.primary} />
                  </View>
                  <View style={styles.presetText}>
                    <Text
                      style={[
                        styles.groupTitle,
                        { color: colors.text, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                      ]}
                      numberOfLines={2}>
                      {t('reminders.createCustomReminder')}
                    </Text>
                    <Text
                      style={[
                        styles.groupMeta,
                        { color: colors.textSecondary, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                      ]}
                      numberOfLines={2}>
                      {t('reminders.customReminderSubtitle')}
                    </Text>
                  </View>
                  <View style={styles.chevronWrap}>
                    <SvgIcon name={disclosure} size={18} color={colors.primary} />
                  </View>
                </View>
              </Clickable>
            </ScrollView>
            <View style={[styles.sheetFooterBar, { backgroundColor: colors.background, borderTopColor: border_muted, paddingBottom: footer_safe_pad }]}>
              <Clickable
                onPress={close_add_modal}
                accessibilityLabel={t('common.cancel')}
                accessibilityRole="button"
                useNativePressable
                haptics
                contentBorderRadius={appTheme.borderRadius.lg}
                contentStyle={styles.sheetFooterBtnContent}
                style={styles.sheetFooterBtnFull}
                pressScale={PRESS_PRESETS.listRow.scale}>
                <View style={cancel_secondary_cta.surface}>
                  <Text style={cancel_secondary_cta.label}>{t('common.cancel')}</Text>
                </View>
              </Clickable>
            </View>
          </View>
        ) : (
          <View style={styles.sheetAddRoot}>
            <View style={styles.sheetListIntro}>
              <Text
                style={[
                  styles.sheetKicker,
                  { color: colors.textLight, textAlign: text_align, writingDirection: rtl.writingDirection },
                ]}
                numberOfLines={1}>
                {t('reminders.customSheetKicker')}
              </Text>
              <Text
                style={[
                  styles.sheetLead,
                  { color: colors.textSecondary, textAlign: text_align, writingDirection: rtl.writingDirection },
                ]}
                numberOfLines={3}>
                {t('reminders.setTitleIconColorFrequency')}
              </Text>
            </View>
            <ScrollView
              ref={custom_scroll_ref}
              style={styles.customScroll}
              contentContainerStyle={[
                styles.customScrollContent,
                { paddingBottom: appTheme.spacing.md + (Platform.OS === 'android' ? keyboard_inset : 0) },
              ]}
              showsVerticalScrollIndicator
              nestedScrollEnabled
              scrollEnabled={custom_scroll_enabled}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="on-drag"
              automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
              bounces
              overScrollMode="always">
              <Input
                placeholder={t('reminders.titlePlaceholder')}
                value={custom_form.label}
                onChangeText={label_text => set_custom_form(f => ({ ...f, label: label_text }))}
                maxLength={60}
                containerStyle={{ marginBottom: 0 }}
              />
              <Text
                style={[
                  styles.fieldKicker,
                  { color: colors.textLight, marginTop: appTheme.spacing.lg, textAlign: text_align, writingDirection: rtl.writingDirection },
                ]}>
                {t('reminders.iconLabel')}
              </Text>
              <View style={styles.iconGrid}>
                {REMINDER_ICON_OPTIONS.map(name => (
                  <Clickable
                    key={name}
                    onPress={() => set_custom_form(f => ({ ...f, icon: name }))}
                    accessibilityLabel={`Icon ${name}`}
                    accessibilityRole="button">
                    <View
                      style={[
                        styles.iconGridItem,
                        {
                          backgroundColor: custom_form.icon === name ? (custom_form.icon_color || colors.primary) + '25' : colors.borderLight + '40',
                          minWidth: 52,
                          minHeight: 52,
                        },
                      ]}>
                      <SvgIcon name={name as any} size={24} color={custom_form.icon === name ? custom_form.icon_color || colors.primary : colors.textSecondary} />
                    </View>
                  </Clickable>
                ))}
              </View>
              <Text
                style={[
                  styles.fieldKicker,
                  { color: colors.textLight, marginTop: appTheme.spacing.lg, textAlign: text_align, writingDirection: rtl.writingDirection },
                ]}>
                {t('reminders.iconColorLabel')}
              </Text>
              <View style={styles.colorRow}>
                {REMINDER_COLOR_OPTIONS.map(hex => (
                  <Clickable
                    key={hex}
                    onPress={() => set_custom_form(f => ({ ...f, icon_color: hex }))}
                    accessibilityLabel={`Color ${hex}`}
                    accessibilityRole="button">
                    <View
                      style={[
                        styles.colorDot,
                        { backgroundColor: hex, minWidth: 44, minHeight: 44 },
                        custom_form.icon_color === hex && [styles.colorDotSelectedRing, { shadowColor: colors.shadowColor }],
                      ]}
                    />
                  </Clickable>
                ))}
              </View>
            </ScrollView>
            <View style={styles.frequencyPickerBlock}>
              <Text
                style={[
                  styles.fieldKicker,
                  { color: colors.textLight, textAlign: text_align, writingDirection: rtl.writingDirection },
                ]}>
                {t('reminders.frequencyLabel')}
              </Text>
              <View
                style={[
                  styles.frequencyWheelSleeve,
                  { backgroundColor: hexToRgba(colors.primary, 0.08) as string, borderColor: border_muted },
                ]}
                accessible
                accessibilityLabel={t('reminders.frequencyPickerA11y').replace(
                  '{{label}}',
                  format_interval(REMINDER_INTERVAL_OPTIONS[interval_index]?.value ?? custom_form.interval_minutes),
                )}>
                <WheelPicker
                  {...interval_wheel_theme}
                  valueMin={0}
                  valueMax={REMINDER_INTERVAL_OPTIONS.length - 1}
                  value={interval_index}
                  onValueChange={on_interval_index_change}
                  onSliderGestureActiveChange={active => set_custom_scroll_enabled(!active)}
                  readoutAlign="start"
                  leadingReadout={
                    <Text
                      style={[
                        styles.frequencyReadout,
                        { color: colors.text, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                      ]}
                      numberOfLines={2}>
                      {format_interval(REMINDER_INTERVAL_OPTIONS[interval_index]?.value ?? custom_form.interval_minutes)}
                    </Text>
                  }
                />
              </View>
            </View>
            <View
              style={[
                styles.sheetFooterActions,
                {
                  backgroundColor: colors.background,
                  borderTopColor: border_muted,
                  paddingBottom: footer_safe_pad + (Platform.OS === 'android' ? keyboard_inset : 0),
                },
              ]}>
              <Clickable
                onPress={close_add_modal}
                accessibilityLabel={t('common.cancel')}
                accessibilityRole="button"
                useNativePressable
                haptics
                pressScale={PRESS_PRESETS.listRow.scale}
                contentBorderRadius={appTheme.borderRadius.lg}
                style={styles.sheetFooterBtnOuter}
                contentStyle={styles.sheetFooterBtnContent}>
                <View style={cancel_secondary_cta.surface}>
                  <Text style={cancel_secondary_cta.label}>{t('common.cancel')}</Text>
                </View>
              </Clickable>
              <Clickable
                onPress={save_custom_reminder}
                accessibilityLabel={t('common.save')}
                accessibilityRole="button"
                useNativePressable
                haptics
                hapticType="impactLight"
                contentBorderRadius={appTheme.borderRadius.lg}
                style={styles.sheetFooterBtnOuter}
                contentStyle={styles.sheetFooterBtnContent}>
                <View style={save_primary_cta.surface}>
                  <Text style={save_primary_cta.label}>{t('common.save')}</Text>
                </View>
              </Clickable>
            </View>
          </View>
        )}
      </BottomSheetPopup>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: card_padding,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 0,
  },
  topRowCollapsed: {
    marginBottom: 0,
  },
  headerTouchable: {
    flex: 1,
    marginRight: 0,
    minHeight: 44,
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
  expandedHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  inCardRemindersTitle: {
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: 0.2,
    lineHeight: 20,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: '800',
    letterSpacing: 0.3,
    marginBottom: 2,
    textTransform: 'capitalize',
  },
  cardSubtitle: {
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 20,
  },
  addPill: {
    borderRadius: 18,
    aspectRatio: 1,
    minWidth: 48,
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyBlock: {
    marginTop: 2,
    paddingVertical: 28,
    paddingHorizontal: 20,
    borderRadius: appTheme.borderRadius.md - 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  emptyHeading: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  emptyHint: {
    fontSize: 13,
  },
  list: {
    marginTop: 0,
  },
  /** Full width so the inner row spans the reminders list (Clickable wraps content in a column inner). */
  reminderRowClickable: {
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: card_padding / 2,
    paddingHorizontal: card_padding / 2,
    gap: card_padding,
  },
  rowIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowBody: { flex: 1, minWidth: 0, justifyContent: 'center' },
  rowLabel: { fontSize: 15, fontWeight: '600', lineHeight: 20 },
  rowMeta: { fontSize: 12, marginTop: 3, lineHeight: 16, fontWeight: '500' },
  sheetAddRoot: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  sheetListIntro: {
    flexShrink: 0,
    paddingHorizontal: card_padding,
    paddingTop: appTheme.spacing.sm,
    paddingBottom: appTheme.spacing.md,
  },
  sheetKicker: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.85,
    textTransform: 'uppercase' as const,
    marginBottom: 6,
  },
  sheetLead: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '500',
  },
  presetScroll: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  presetScrollContent: {
    paddingTop: 0,
    paddingBottom: appTheme.spacing.lg,
    paddingHorizontal: card_padding,
  },
  sheetGroup: {
    borderRadius: DASH_STUDIO_RADIUS,
    overflow: 'hidden',
    ...appTheme.shadows.xs,
  },
  groupSep: { height: 8, width: '100%' },
  groupRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 68,
    paddingVertical: appTheme.spacing.md,
    paddingHorizontal: appTheme.spacing.lg,
    gap: appTheme.spacing.md,
  },
  groupRowClickable: { width: '100%' as const, alignSelf: 'stretch' as const },
  groupRowClickableContent: { width: '100%' as const },
  groupIcon: {
    width: 52,
    height: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  groupTitle: { fontSize: 16, fontWeight: '700', letterSpacing: 0.1, lineHeight: 22 },
  groupMeta: { fontSize: 13, fontWeight: '600', marginTop: 4, letterSpacing: 0.1, lineHeight: 18, opacity: 0.9 },
  chevronWrap: { width: 28, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  customBlock: { width: '100%' as const, marginTop: appTheme.spacing.lg },
  customCreateCard: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 64,
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 14,
    borderRadius: DASH_STUDIO_RADIUS,
    width: '100%' as const,
  },
  customIconBox: {
    width: 52,
    height: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  presetText: { flex: 1, minWidth: 0, justifyContent: 'center' },
  sheetFooterBar: {
    flexShrink: 0,
    paddingTop: appTheme.spacing.md,
    paddingHorizontal: card_padding,
    paddingBottom: appTheme.spacing.sm,
    width: '100%' as const,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  customScroll: {
    flex: 1,
    minHeight: 0,
    width: '100%' as const,
  },
  customScrollContent: {
    paddingBottom: appTheme.spacing.lg,
    paddingTop: appTheme.spacing.xs,
    paddingHorizontal: card_padding,
  },
  fieldKicker: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.7,
    textTransform: 'uppercase' as const,
    marginBottom: appTheme.spacing.sm,
  },
  iconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: appTheme.spacing.md,
  },
  iconGridItem: {
    width: 54,
    height: 54,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorRow: { flexDirection: 'row', flexWrap: 'wrap', gap: appTheme.spacing.md, marginTop: appTheme.spacing.sm },
  colorDot: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  colorDotSelectedRing: {
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 2,
    elevation: 2,
  },
  intervalWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: appTheme.spacing.sm, marginTop: appTheme.spacing.xs },
  intervalChip: {
    paddingVertical: appTheme.spacing.sm + 2,
    paddingHorizontal: appTheme.spacing.lg,
    borderRadius: 20,
    minHeight: 44,
    minWidth: 88,
    justifyContent: 'center',
    borderWidth: StyleSheet.hairlineWidth,
  },
  intervalChipText: { fontSize: 14, fontWeight: '700', lineHeight: 18 },
  frequencyPickerBlock: {
    flexShrink: 0,
    paddingHorizontal: card_padding,
    paddingTop: appTheme.spacing.sm,
    paddingBottom: appTheme.spacing.xs,
    width: '100%' as const,
  },
  frequencyWheelSleeve: {
    borderRadius: DASH_STUDIO_RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: appTheme.spacing.sm,
    paddingBottom: appTheme.spacing.xs,
    overflow: 'hidden',
    minHeight: 112,
    width: '100%' as const,
  },
  frequencyReadout: {
    fontSize: 17,
    fontWeight: '700',
    lineHeight: 24,
    flexShrink: 1,
  },
  sheetFooterActions: {
    flexDirection: 'row',
    flexShrink: 0,
    alignItems: 'stretch',
    gap: appTheme.spacing.md,
    paddingTop: appTheme.spacing.md,
    paddingHorizontal: card_padding,
    borderTopWidth: StyleSheet.hairlineWidth,
    width: '100%' as const,
  },
  sheetFooterBtnOuter: {
    flex: 1,
    minWidth: 0,
  },
  sheetFooterBtnContent: {
    alignSelf: 'stretch',
    width: '100%' as const,
  },
  sheetFooterBtnFull: {
    alignSelf: 'stretch',
    width: '100%' as const,
  },
});
